import React, { useState, useEffect, useRef } from "react";
import api, { API } from "@/lib/api";
import { inr, ddmmyyyy, inrPlain } from "@/lib/format";
import { Plus, MagnifyingGlass, Pencil, Trash, DownloadSimple, UploadSimple, FileXls, FilePdf, FileCsv, CaretDown, LinkSimple } from "@phosphor-icons/react";
import TransactionModal from "@/components/TransactionModal";
import { toast } from "sonner";
import { useBooks } from "@/context/BookContext";

export default function Transactions() {
  const { activeId, activeBook } = useBooks();
  const isHaya = (activeBook?.name || "").toLowerCase() === "haya";
  const isAK = (activeBook?.name || "").toLowerCase() === "ak accounts";
  const [txs, setTxs] = useState([]);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const params = {};
    if (activeId) params.book_id = activeId;
    if (q) params.q = q;
    if (mode) params.mode = mode;
    const { data } = await api.get("/transactions", { params });
    setTxs(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, activeId]);

  // Debounced live search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const del = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const exportAs = async (fmt) => {
    setExportOpen(false);
    try {
      const params = new URLSearchParams({ format: fmt });
      if (activeId) params.set("book_id", activeId);
      const res = await fetch(`${API}/transactions/export?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) { toast.error("Export failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jespay_ledger.${fmt === "xlsx" ? "xlsx" : fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const url = activeId ? `/transactions/import?book_id=${activeId}` : "/transactions/import";
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.failed > 0) {
        toast.warning(`Imported ${data.inserted}, ${data.failed} failed. ${(data.errors || []).slice(0,2).join(" · ")}`);
      } else {
        toast.success(`Imported ${data.inserted} rows`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#2cb4a6]">Cash · UPI · DTT · Bank{activeBook ? ` · ${activeBook.name}` : ""}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-none">Ledger.</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={importFile} />
          <button onClick={() => fileRef.current?.click()} className="brut-btn brut-btn-secondary flex items-center gap-2" data-testid="import-btn">
            <UploadSimple size={16} weight="bold" /> Import
          </button>
          <div className="relative">
            <button onClick={() => setExportOpen((v) => !v)} className="brut-btn brut-btn-secondary flex items-center gap-2" data-testid="export-btn">
              <DownloadSimple size={16} weight="bold" /> Export <CaretDown size={12} weight="bold" />
            </button>
            {exportOpen && (
              <div className="absolute right-0 mt-1 z-30 bg-white border-2 border-[#0f1b4c] shadow-[4px_4px_0_#0f1b4c] w-52" data-testid="export-menu">
                <button onClick={() => exportAs("xlsx")} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#f4f7fb] font-bold text-sm border-b border-[#0f1b4c]/10" data-testid="export-xlsx">
                  <FileXls size={18} weight="bold" color="#16a063" /> Excel (.xlsx)
                </button>
                <button onClick={() => exportAs("pdf")} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#f4f7fb] font-bold text-sm border-b border-[#0f1b4c]/10" data-testid="export-pdf">
                  <FilePdf size={18} weight="bold" color="#c53030" /> PDF (.pdf)
                </button>
                <button onClick={() => exportAs("csv")} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#f4f7fb] font-bold text-sm" data-testid="export-csv">
                  <FileCsv size={18} weight="bold" color="#1e2a6b" /> CSV (.csv)
                </button>
              </div>
            )}
          </div>
          <button onClick={() => { setEditing(null); setModalOpen(true); }} className="brut-btn brut-btn-primary flex items-center gap-2" data-testid="add-tx-btn">
            <Plus size={16} weight="bold" /> Add Transaction
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="brut-card p-4 flex gap-3 flex-wrap items-end">
        <form onSubmit={onSearch} className="flex-1 min-w-[200px]">
          <label className="brut-label block mb-2">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5c59]" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, purpose, bank…" className="brut-input pl-9" />
          </div>
        </form>
        <div>
          <label className="brut-label block mb-2">Mode</label>
          <div className="flex gap-2">
            {[["", "All"], ["cash", "Cash"], ["upi", "UPI"], ["dtt", "DTT"]].map(([v, l]) => (
              <button key={v} data-testid={`filter-mode-${v || "all"}`} onClick={() => setMode(v)}
                className={`brut-btn text-xs ${mode === v ? "brut-btn-primary" : "brut-btn-secondary"}`}>{l}</button>
            ))}
          </div>
        </div>
        <button type="button" onClick={load} className="brut-btn brut-btn-secondary text-xs" data-testid="apply-filters">Apply</button>
      </div>

      {/* Table */}
      <div className="brut-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="brut" data-testid="tx-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Mode</th>
                <th>Name</th>
                {isHaya && <th className="text-right">Actual</th>}
                {isHaya && <th className="text-right">Comm %</th>}
                <th className="text-right">Amount</th>
                <th className="text-right">{isAK ? "Balance" : "Cash Balance"}</th>
                <th>Bank</th>
                <th>Purpose</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.id} data-testid={`tx-row-${t.id}`}>
                  <td className="font-mono-fin">{ddmmyyyy(t.date)}</td>
                  <td>
                    <span className={`chip ${t.mode === "cash" ? "chip-cash" : t.mode === "upi" ? "chip-upi" : "chip-dtt"}`}>{t.mode}</span>
                    {t.mode === "dtt" && t.rate != null && (
                      <div className="text-[10px] text-[#4a5578] font-mono-fin mt-1">
                        {t.units} × ₹{t.rate}
                      </div>
                    )}
                  </td>
                  <td className="font-bold">
                    {t.name}
                    {t.linked_tx_id && (
                      <span
                        title="Linked to another ledger book (auto-mirror)"
                        className="inline-flex items-center gap-1 ml-2 chip chip-pos"
                        data-testid={`tx-linked-${t.id}`}
                      >
                        <LinkSimple size={10} weight="bold" /> linked
                      </span>
                    )}
                  </td>
                  {isHaya && (
                    <td className="text-right font-mono-fin">
                      {t.actual_amount != null ? inrPlain(t.actual_amount) : "—"}
                    </td>
                  )}
                  {isHaya && (
                    <td className="text-right font-mono-fin text-[#4a5578]">
                      {t.commission_pct != null ? `${t.commission_pct}%` : "—"}
                    </td>
                  )}
                  <td className={`text-right font-mono-fin ${t.amount < 0 ? "num-neg" : "num-pos"}`}>{inr(t.amount)}</td>
                  <td className="text-right font-mono-fin">{t.running_balance != null ? inrPlain(t.running_balance) : "—"}</td>
                  <td className="text-[#5c5c59]">{t.bank || "—"}</td>
                  <td className="text-[#5c5c59]">{t.purpose || "—"}</td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setEditing(t); setModalOpen(true); }} className="brut-btn brut-btn-secondary p-2" data-testid={`edit-tx-${t.id}`}>
                        <Pencil size={14} weight="bold" />
                      </button>
                      <button onClick={() => del(t.id)} className="brut-btn brut-btn-danger p-2" data-testid={`del-tx-${t.id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {txs.length === 0 && (
                <tr><td colSpan={isHaya ? 10 : 8} className="text-center py-10 text-[#5c5c59]">No transactions match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <TransactionModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} onSaved={load} />
    </div>
  );
}
